// Progression System - Levels, XP, Achievements
import { loadUserProfile, saveUserProfile } from './storageService';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockedAt?: number;
  tier: 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';
  xpReward: number;
}

export interface ProgressionData {
  level: number;
  currentXP: number;
  totalXP: number;
  discoveryCount: number;
  combinationCount: number;
  achievements: Record<string, Achievement>;
  milestones: string[];
}

// XP required for each level (exponential growth)
export const getXPForLevel = (level: number): number => {
  return Math.floor(100 * Math.pow(1.5, level - 1));
};

// Get current level from total XP
export const getLevelFromXP = (totalXP: number): number => {
  let level = 1;
  let xpRequired = 0;

  while (xpRequired <= totalXP) {
    xpRequired += getXPForLevel(level);
    if (xpRequired <= totalXP) {
      level++;
    }
  }

  return level;
};

// All possible achievements
export const ACHIEVEMENTS: Record<string, Omit<Achievement, 'unlocked' | 'unlockedAt'>> = {
  // Discovery Achievements
  first_discovery: {
    id: 'first_discovery',
    name: 'First Steps',
    description: 'Discover your first element',
    icon: '🌟',
    tier: 'bronze',
    xpReward: 50,
  },
  discover_10: {
    id: 'discover_10',
    name: 'Novice Alchemist',
    description: 'Discover 10 elements',
    icon: '⚗️',
    tier: 'bronze',
    xpReward: 100,
  },
  discover_25: {
    id: 'discover_25',
    name: 'Apprentice',
    description: 'Discover 25 elements',
    icon: '🔮',
    tier: 'silver',
    xpReward: 250,
  },
  discover_50: {
    id: 'discover_50',
    name: 'Adept Crafter',
    description: 'Discover 50 elements',
    icon: '✨',
    tier: 'gold',
    xpReward: 500,
  },
  discover_100: {
    id: 'discover_100',
    name: 'Master Alchemist',
    description: 'Discover 100 elements',
    icon: '🏆',
    tier: 'platinum',
    xpReward: 1000,
  },
  discover_all: {
    id: 'discover_all',
    name: 'Omniscient',
    description: 'Discover all 148 elements',
    icon: '👑',
    tier: 'diamond',
    xpReward: 5000,
  },

  // Tier Achievements
  rare_element: {
    id: 'rare_element',
    name: 'Rare Find',
    description: 'Discover your first rare element (Tier 4+)',
    icon: '💎',
    tier: 'silver',
    xpReward: 200,
  },
  legendary_element: {
    id: 'legendary_element',
    name: 'Legendary Crafter',
    description: 'Discover your first legendary element (Tier 6+)',
    icon: '🌠',
    tier: 'gold',
    xpReward: 500,
  },

  // Combination Achievements
  combo_master: {
    id: 'combo_master',
    name: 'Combo Master',
    description: 'Perform 100 combinations',
    icon: '🎯',
    tier: 'silver',
    xpReward: 300,
  },
  speed_crafter: {
    id: 'speed_crafter',
    name: 'Speed Crafter',
    description: 'Discover 5 elements in under 1 minute',
    icon: '⚡',
    tier: 'gold',
    xpReward: 400,
  },

  // Game Mode Achievements
  gauntlet_champion: {
    id: 'gauntlet_champion',
    name: 'Gauntlet Champion',
    description: 'Complete all Gauntlet levels',
    icon: '🏅',
    tier: 'platinum',
    xpReward: 1000,
  },
  time_attack_master: {
    id: 'time_attack_master',
    name: 'Time Lord',
    description: 'Score over 1000 in Time Attack',
    icon: '⏱️',
    tier: 'gold',
    xpReward: 500,
  },
  daily_streak_7: {
    id: 'daily_streak_7',
    name: 'Dedicated',
    description: 'Complete 7 daily puzzles in a row',
    icon: '📅',
    tier: 'gold',
    xpReward: 700,
  },

  // Special Achievements
  night_owl: {
    id: 'night_owl',
    name: 'Night Owl',
    description: 'Play between midnight and 4 AM',
    icon: '🦉',
    tier: 'bronze',
    xpReward: 100,
  },
  social_butterfly: {
    id: 'social_butterfly',
    name: 'Social Butterfly',
    description: 'Share your progress 5 times',
    icon: '🦋',
    tier: 'silver',
    xpReward: 200,
  },
};

// Initialize progression data
export const initProgression = (): ProgressionData => {
  const profile = loadUserProfile();

  if (!profile.progression) {
    const progression: ProgressionData = {
      level: 1,
      currentXP: 0,
      totalXP: 0,
      discoveryCount: 0,
      combinationCount: 0,
      achievements: {},
      milestones: [],
    };

    // Initialize all achievements as locked
    Object.values(ACHIEVEMENTS).forEach(achievement => {
      progression.achievements[achievement.id] = {
        ...achievement,
        unlocked: false,
      };
    });

    // Save to profile
    saveUserProfile({ progression });
    return progression;
  }

  return profile.progression;
};

// Add XP and check for level up
export const addXP = (amount: number): { leveledUp: boolean; newLevel: number; xpGained: number } => {
  const progression = initProgression();
  const oldLevel = progression.level;

  progression.currentXP += amount;
  progression.totalXP += amount;

  // Check for level up
  const xpForNextLevel = getXPForLevel(progression.level);

  if (progression.currentXP >= xpForNextLevel) {
    progression.level++;
    progression.currentXP -= xpForNextLevel;
  }

  // Save
  saveUserProfile({ progression });

  return {
    leveledUp: progression.level > oldLevel,
    newLevel: progression.level,
    xpGained: amount,
  };
};

// Unlock achievement
export const unlockAchievement = (achievementId: string): { unlocked: boolean; achievement?: Achievement } => {
  const progression = initProgression();
  const achievement = progression.achievements[achievementId];

  if (!achievement || achievement.unlocked) {
    return { unlocked: false };
  }

  // Unlock achievement
  achievement.unlocked = true;
  achievement.unlockedAt = Date.now();

  // Award XP
  const { leveledUp } = addXP(achievement.xpReward);

  // Save
  saveUserProfile({ progression });

  return { unlocked: true, achievement: { ...achievement } };
};

// Check and unlock achievements based on current state
export const checkAchievements = (discoveredElements: Set<string>, totalCombinations: number) => {
  const progression = initProgression();
  const unlocked: Achievement[] = [];

  // Discovery count achievements
  const discoveryCount = discoveredElements.size;
  progression.discoveryCount = discoveryCount;

  if (discoveryCount >= 1 && !progression.achievements.first_discovery?.unlocked) {
    const result = unlockAchievement('first_discovery');
    if (result.unlocked && result.achievement) unlocked.push(result.achievement);
  }
  if (discoveryCount >= 10 && !progression.achievements.discover_10?.unlocked) {
    const result = unlockAchievement('discover_10');
    if (result.unlocked && result.achievement) unlocked.push(result.achievement);
  }
  if (discoveryCount >= 25 && !progression.achievements.discover_25?.unlocked) {
    const result = unlockAchievement('discover_25');
    if (result.unlocked && result.achievement) unlocked.push(result.achievement);
  }
  if (discoveryCount >= 50 && !progression.achievements.discover_50?.unlocked) {
    const result = unlockAchievement('discover_50');
    if (result.unlocked && result.achievement) unlocked.push(result.achievement);
  }
  if (discoveryCount >= 100 && !progression.achievements.discover_100?.unlocked) {
    const result = unlockAchievement('discover_100');
    if (result.unlocked && result.achievement) unlocked.push(result.achievement);
  }
  if (discoveryCount >= 148 && !progression.achievements.discover_all?.unlocked) {
    const result = unlockAchievement('discover_all');
    if (result.unlocked && result.achievement) unlocked.push(result.achievement);
  }

  // Combination count
  progression.combinationCount = totalCombinations;
  if (totalCombinations >= 100 && !progression.achievements.combo_master?.unlocked) {
    const result = unlockAchievement('combo_master');
    if (result.unlocked && result.achievement) unlocked.push(result.achievement);
  }

  // Save progression
  saveUserProfile({ progression });

  return unlocked;
};

// Get progression stats
export const getProgressionStats = () => {
  const progression = initProgression();
  const xpForNextLevel = getXPForLevel(progression.level);
  const progressToNextLevel = (progression.currentXP / xpForNextLevel) * 100;

  const unlockedAchievements = Object.values(progression.achievements).filter(a => a.unlocked);
  const totalAchievements = Object.values(progression.achievements).length;

  return {
    level: progression.level,
    currentXP: progression.currentXP,
    xpForNextLevel,
    progressToNextLevel,
    totalXP: progression.totalXP,
    discoveryCount: progression.discoveryCount,
    combinationCount: progression.combinationCount,
    achievementCount: unlockedAchievements.length,
    totalAchievements,
    achievementProgress: (unlockedAchievements.length / totalAchievements) * 100,
  };
};

// Get all achievements (sorted by tier)
export const getAllAchievements = (): Achievement[] => {
  const progression = initProgression();
  return Object.values(progression.achievements).sort((a, b) => {
    const tierOrder = { diamond: 5, platinum: 4, gold: 3, silver: 2, bronze: 1 };
    return tierOrder[b.tier] - tierOrder[a.tier];
  });
};
